// SPDX-License-Identifier: AGPL-3.0-or-later

import {RoomPositions} from '@app/features/voice_scene/net/PositionBuffer';
import type {RoomWalk} from '@app/features/voice_scene/scene/VoiceSceneTypes';
import {useEffect, useState} from 'react';

// After the last walker sits down the walking sounds get this long more: one more update puts everyone back on their
// seats and plays the sit.
export const WALK_TAIL_MS = 500;

export interface WalkActivity {
	// My character walks.
	local: boolean;
	// Anyone walks (me or someone whose positions arrive), until WALK_TAIL_MS after the last one sat down.
	any: boolean;
}

const IDLE: WalkActivity = {local: false, any: false};

// Who walks in the room, for the timers that only run then (the position sender, the walking sounds, the sit hint):
// seated in a room where nobody walks, none of them runs. Renders only when an answer changes, never per packet.
export function useWalkActivity(walk: RoomWalk | null): WalkActivity {
	const [activity, setActivity] = useState(IDLE);
	useEffect(() => {
		if (!walk) return;
		let shown = IDLE;
		let tail: ReturnType<typeof setTimeout> | null = null;
		const show = (next: WalkActivity): void => {
			if (next.local === shown.local && next.any === shown.any) return;
			shown = next;
			setActivity(next);
		};
		const read = (): void => {
			const local = walk.getPose() !== null;
			if (local || RoomPositions.size > 0) {
				if (tail !== null) clearTimeout(tail);
				tail = null;
				show({local, any: true});
				return;
			}
			show({local: false, any: shown.any});
			if (shown.any && tail === null)
				tail = setTimeout(() => {
					tail = null;
					show(IDLE);
				}, WALK_TAIL_MS);
		};
		read();
		const offWalk = walk.onWalkingChange(read);
		const offOthers = RoomPositions.subscribe(read);
		return () => {
			offWalk();
			offOthers();
			if (tail !== null) clearTimeout(tail);
			setActivity(IDLE);
		};
	}, [walk]);
	return walk ? activity : IDLE;
}
